import React from 'react'
import { useParams } from 'react-router-dom'

import { Box, Button, List, Tag, Text, useToggle } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { DropableBox } from 'modules/core/components/DragAndDrop'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import ItemAssignees from 'modules/processes/components/ItemAssignees'
import { AssigneeStrategy } from 'modules/processes/components/ItemAssignees/types'
import ItemWizard, {
  ItemConfigurationType,
  WizardTypes,
  mapItemTypeToWizardType,
} from 'modules/processes/components/ItemWizard'
import { useSteps } from 'modules/processes/components/ProcessSettings/Steps/StepsProvider'
import TaskButton from 'modules/processes/components/TaskButton'
import type { Item } from 'modules/processes/domain'

import DeleteItemConfirmationModal from './DeleteItemConfirmationModal'
import { DragableItem } from './DragableItem'
import { ItemIcon } from './ItemIcon'
import LinkedConfigurationRemoved from './LinkedConfigurationRemoved'

interface Props {
  stepId: number
  items: Item[]
  processId: number
  isEditable: boolean
  position: number
  updateItemAndSteps: () => void
}

const Items = ({ items, stepId, processId, isEditable, position, updateItemAndSteps }: Props) => {
  const [open, toggleSidebar] = useToggle(false)
  const [wizardType, setWizardType] = React.useState<WizardTypes | null>(null)
  const [selectedItemId, setItemId] = React.useState<number | null>(null)
  const [showDeleteModal, setShowDeleteModal] = React.useState<boolean>(false)
  const { category } = useParams()

  const { itemsHaveChanged, stepsHaveChanged, steps } = useSteps()

  const updateBeforeModify = () => {
    if (itemsHaveChanged || stepsHaveChanged) updateItemAndSteps()
  }

  const toggleOpen = (value: boolean) => {
    // As we are using a PopoverMenu, we need to wait for the animation to finish before opening the wizard
    // Otherwise, when the PopoverMenu is closed, the wizard won't be opened because
    // the PopoverMenu is throwing the event to click outside the menu
    setTimeout(() => {
      toggleSidebar(value)()
    }, 25)
  }

  const deleteItem = (id: number) => {
    updateBeforeModify()
    setItemId(id)
    setShowDeleteModal(true)
  }

  const editItem = (id: number, configurationType?: string | null) => {
    const selectedItemWizardType = mapItemTypeToWizardType(configurationType)

    updateBeforeModify()
    setWizardType(selectedItemWizardType)
    setItemId(id)
    toggleOpen(true)
  }

  const addNewTask = () => {
    updateBeforeModify()
    setItemId(null)
    setWizardType('task')

    setTimeout(() => {
      toggleOpen(true)
    }, 25)

    track(trackingEvents.processes.processTaskCreationOpened, {
      category: category,
      isWizard: true,
    })
  }

  const addNewAction = () => {
    updateBeforeModify()
    setItemId(null)
    setWizardType('action')
    toggleOpen(true)
    track(trackingEvents.processes.createActionStarted, {
      isWizard: true,
      category,
    })
  }

  const selectedItem = items.find((item) => item.id === selectedItemId)

  const editTitle = (configurationType: string) =>
    configurationType == ItemConfigurationType['Processes::Action']
      ? i18n.t('processes.process_settings.actions.edit')
      : i18n.t('processes.process_settings.tasks.edit')

  const deleteTitle = (configurationType: string) =>
    configurationType == ItemConfigurationType['Processes::Action']
      ? i18n.t('processes.process_settings.actions.delete')
      : i18n.t('processes.process_settings.tasks.delete')

  return (
    <Box>
      <DropableBox droppableId={position.toString()} type="item">
        {items.length === 0 && <Box height="s2" />}
        <Box gap="s12">
          {items.map((item, index) => (
            <DragableItem key={`item-${item.id.toString()}`} position={index} id={item.id}>
              <List.ClickableItem
                navigateTo={() => editItem(item.id, item.configurationType)}
                key={item.id}
              >
                <Box flexDirection="row" gap="s16" alignItems="center">
                  <Box>
                    <ItemIcon configurationType={item.configurationType} />
                  </Box>
                  <Text.Normal ellipsis weight="medium" lineClamp={2}>
                    {item.title}
                  </Text.Normal>

                  {item.configurationType == ItemConfigurationType['Processes::Action'] && (
                    <LinkedConfigurationRemoved item={item} />
                  )}
                </Box>
                <Box gap="s16" flexDirection="row" alignItems="center">
                  {item.mandatory && (
                    <Tag inline type={Tag.Types.champagne}>
                      {i18n.t('processes.materialize_process.detail.mandatory')}
                    </Tag>
                  )}
                  <ItemAssignees
                    accessIds={item.itemAssignee?.assigneeIds}
                    assigneeStrategy={item.itemAssignee?.assigneeStrategy as AssigneeStrategy} // eslint-disable-line no-type-assertion/no-type-assertion
                    itemAssigneeIds={item.itemAssignee?.assigneeIds}
                  />
                  {isEditable && (
                    <List.ActionItem>
                      <PopoverMenu type="list">
                        <PopoverMenu.Trigger>
                          <Button.Decorative type="secondary" iconName="ellipsis" />
                        </PopoverMenu.Trigger>
                        <PopoverMenu.Content side="bottom" align="center">
                          <PopoverMenu.TouchableOption
                            icon="pencil"
                            title={editTitle(String(item.configurationType))}
                            onClick={() => editItem(item.id, item.configurationType)}
                          />
                          <PopoverMenu.TouchableOption
                            icon="delete"
                            title={deleteTitle(String(item.configurationType))}
                            onClick={() => deleteItem(item.id)}
                            danger
                          />
                        </PopoverMenu.Content>
                      </PopoverMenu>
                    </List.ActionItem>
                  )}
                </Box>
              </List.ClickableItem>
            </DragableItem>
          ))}
        </Box>
      </DropableBox>
      {isEditable && (
        <Box paddingY="s16" paddingLeft="s24">
          <List.ActionItem>
            <PopoverMenu type="list">
              <PopoverMenu.Trigger>
                <TaskButton icon="add">{i18n.t('processes.process_settings.item.new')}</TaskButton>
              </PopoverMenu.Trigger>
              <PopoverMenu.Content side="bottom" align="center">
                <PopoverMenu.TouchableOption
                  title={i18n.t('processes.process_settings.item.new_task')}
                  onClick={addNewTask}
                />
                <PopoverMenu.TouchableOption
                  title={i18n.t('processes.process_settings.item.new_action')}
                  onClick={addNewAction}
                />
              </PopoverMenu.Content>
            </PopoverMenu>
          </List.ActionItem>
        </Box>
      )}

      {open && (
        <ItemWizard
          onClose={() => {
            toggleOpen(false)
            setWizardType(null)
          }}
          type={wizardType}
          stepId={stepId}
          item={selectedItem}
          steps={steps}
        />
      )}
      {selectedItemId && selectedItem && showDeleteModal && (
        <DeleteItemConfirmationModal
          processId={processId}
          onClose={() => setShowDeleteModal(false)}
          item={selectedItem}
        />
      )}
    </Box>
  )
}

export default observer(Items)
